/**
 * Frozen-frame sidecar — the cross-session "last screen" for a task's
 * browser pane. browserStore.frozenFrameByTask is deliberately unpersisted
 * (the server is SOT for view state); this sidecar holds the LAST-KNOWN
 * frame per task so a revisited task can show what the browser looked like,
 * asleep, with a Wake button (founder 08-12).
 *
 * Ownership split: the store is in-session truth (captured full-res on
 * suspend, cleared on live), the sidecar is across-session truth (written
 * at capture, downscaled, never cleared by stream transitions — only LRU
 * eviction or task deletion removes it).
 *
 * Every localStorage failure (quota, privacy mode, corrupt JSON) degrades
 * silently: a lost frame renders the moon card.
 */

const KEY = "vestra-browser-frames-v1";
export const SIDECAR_MAX_TASKS = 6;
/** Disk copies are downscaled — the asleep backdrop tolerates softness and
 *  localStorage does not tolerate six 1440px JPEGs. */
const SIDECAR_MAX_WIDTH = 640;
const SIDECAR_JPEG_QUALITY = 0.7;

interface SidecarEntry {
  dataUrl: string;
  at: number;
}

/** Parse-once module cache; null = not yet loaded this page lifetime. */
let cache: Record<string, SidecarEntry> | null = null;

function load(): Record<string, SidecarEntry> {
  if (cache) return cache;
  cache = {};
  if (typeof window === "undefined") return cache;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Record<string, SidecarEntry>;
      if (parsed && typeof parsed === "object") {
        for (const [taskId, entry] of Object.entries(parsed)) {
          if (
            entry &&
            typeof entry.dataUrl === "string" &&
            typeof entry.at === "number"
          ) {
            cache[taskId] = entry;
          }
        }
      }
    }
  } catch {
    // Corrupt store — start empty; the next save rewrites it.
  }
  return cache;
}

function persist(frames: Record<string, SidecarEntry>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(frames));
  } catch {
    // Quota / privacy mode — memory cache still serves this page lifetime.
  }
}

export function getSidecarFrame(taskId: string): string | null {
  return load()[taskId]?.dataUrl ?? null;
}

export function saveSidecarFrame(
  taskId: string,
  dataUrl: string,
  now: number = Date.now()
): void {
  const frames = load();
  frames[taskId] = { dataUrl, at: now };
  const ids = Object.keys(frames).sort((a, b) => frames[a].at - frames[b].at);
  while (ids.length > SIDECAR_MAX_TASKS) {
    delete frames[ids.shift() as string];
  }
  persist(frames);
}

export function removeSidecarFrame(taskId: string): void {
  const frames = load();
  if (!(taskId in frames)) return;
  delete frames[taskId];
  persist(frames);
}

/**
 * Draw the video's current frame at sidecar scale. Returns null when the
 * video has no decoded frames or canvas is unavailable (jsdom, tainted
 * surface). Kept here so both capture sites (suspend + tab-hidden) share
 * one downscale.
 */
export function downscaleToSidecarJpeg(video: HTMLVideoElement): string | null {
  try {
    if (!video.videoWidth || !video.videoHeight) return null;
    const scale = Math.min(1, SIDECAR_MAX_WIDTH / video.videoWidth);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", SIDECAR_JPEG_QUALITY);
  } catch {
    return null;
  }
}

/** Test-only: drop the parse-once cache so a fresh load() re-reads disk. */
export function _resetSidecarCacheForTests(): void {
  cache = null;
}
